/**
 * Reporta falhas do browser para o servidor, para poderem ser revistas dias ou
 * semanas depois (painel Django → "Falhas no Browser").
 *
 * Porquê: os piores problemas do PadelMaster nunca saíram do PC do clube —
 * memória cheia a impedir gravações, tokens apagados, jogo não guardado. A app
 * escrevia um aviso na consola (que ninguém abre) e seguia como se nada fosse.
 *
 * Regras de ouro deste ficheiro:
 *   1. NUNCA lançar. Um erro a reportar erros não pode partir o jogo.
 *   2. Funcionar offline: guarda em fila e envia quando houver rede.
 *   3. Não inundar: agrega repetidos e limita o tamanho da fila.
 *   4. Ser leve no armazenamento — foi a falta dele que causou o problema.
 */

import { CONFIG } from '@/config/environment';

export type NivelFalha = 'aviso' | 'erro' | 'critico';

interface FalhaNaFila {
  codigo: string;
  nivel: NivelFalha;
  mensagem: string;
  contexto: Record<string, unknown>;
  dispositivo: string;
  versao_app: string;
  url: string;
  quando: string;
  ocorrencias: number;
}

const CHAVE_FILA = 'pm_falhas_por_enviar';
const CHAVE_DISPOSITIVO = 'padelmaster_device_id'; // já protegida das limpezas
const MAX_NA_FILA = 30;
const JANELA_AGREGACAO_MS = 6 * 60 * 60 * 1000;

/** Id estável do PC, para distinguir "o PC do clube" de "o meu portátil". */
function dispositivo(): string {
  try {
    let id = localStorage.getItem(CHAVE_DISPOSITIVO);
    if (!id) {
      id = `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(CHAVE_DISPOSITIVO, id);
    }
    return id;
  } catch {
    return 'desconhecido';
  }
}

function lerFila(): FalhaNaFila[] {
  try {
    const bruto = localStorage.getItem(CHAVE_FILA);
    if (!bruto) return [];
    const dados = JSON.parse(bruto);
    return Array.isArray(dados) ? (dados as FalhaNaFila[]) : [];
  } catch {
    return [];
  }
}

function escreverFila(fila: FalhaNaFila[]): void {
  try {
    localStorage.setItem(CHAVE_FILA, JSON.stringify(fila.slice(-MAX_NA_FILA)));
  } catch {
    // Se nem isto cabe, o armazenamento está cheio — descartar em silêncio é
    // a escolha certa: nunca sacrificar o jogo para guardar um relatório.
  }
}

let aEnviar = false;

/** Envia a fila. Silencioso por design: offline é normal, não é um erro. */
export async function enviarFalhasPendentes(): Promise<void> {
  if (aEnviar) return;
  const fila = lerFila();
  if (fila.length === 0) return;

  aEnviar = true;
  try {
    const resposta = await fetch(`${CONFIG.BACKEND_URL}/padel/history/falhas/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ falhas: fila }),
      signal: AbortSignal.timeout(8000),
    });
    // 2xx → entregue; 4xx (exceto 429) → o servidor não quer estas entradas,
    // insistir só faria a fila crescer para sempre. Nos dois casos limpamos.
    if (resposta.ok || (resposta.status >= 400 && resposta.status < 500 && resposta.status !== 429)) {
      escreverFila([]);
    }
  } catch {
    // sem rede: fica na fila para a próxima tentativa
  } finally {
    aEnviar = false;
  }
}

/**
 * Regista uma falha. Não lança, não bloqueia, não espera pela rede.
 *
 * @param codigo  identificador estável (ex.: 'storage_quota_cheia') — é por
 *                aqui que se agrupam as ocorrências no painel
 */
export function reportarFalha(
  codigo: string,
  mensagem: string,
  opcoes: { nivel?: NivelFalha; contexto?: Record<string, unknown> } = {}
): void {
  try {
    const agora = Date.now();
    const fila = lerFila();

    // Agregar com uma entrada igual e recente, em vez de encher a fila
    const igual = fila.find(
      (f) => f.codigo === codigo && agora - new Date(f.quando).getTime() < JANELA_AGREGACAO_MS
    );
    if (igual) {
      igual.ocorrencias += 1;
      igual.quando = new Date(agora).toISOString();
      escreverFila(fila);
    } else {
      fila.push({
        codigo,
        nivel: opcoes.nivel ?? 'erro',
        mensagem: String(mensagem ?? '').slice(0, 2000),
        contexto: opcoes.contexto ?? {},
        dispositivo: dispositivo(),
        versao_app: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '',
        url: window.location.pathname,
        quando: new Date(agora).toISOString(),
        ocorrencias: 1,
      });
      escreverFila(fila);
    }

    // Tentar entregar já; se não houver rede fica para depois.
    void enviarFalhasPendentes();
  } catch {
    /* reportar falhas nunca pode ser mais uma falha */
  }
}

/** Liga o envio automático: ao arrancar, ao voltar a haver rede e de hora a hora. */
export function iniciarReporterDeFalhas(): void {
  try {
    void enviarFalhasPendentes();
    window.addEventListener('online', () => void enviarFalhasPendentes());
    window.setInterval(() => void enviarFalhasPendentes(), 60 * 60 * 1000);
  } catch {
    /* ignora */
  }
}
